import React, { useEffect, useState } from "react";
import { Plus, Ticket, Trophy, Trash2, Gift, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const inputCls =
  "bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-[#7fd0f5]/40 h-11 rounded-xl";

const labelCls = "text-[#9fc5e8] text-[0.68rem] font-bold tracking-[0.2em] uppercase";

const AdminRaffles = () => {
  const [raffles, setRaffles] = useState(null);
  const [users, setUsers] = useState([]);
  const [openId, setOpenId] = useState(null);

  // create form
  const [title, setTitle] = useState("");
  const [prize, setPrize] = useState("");
  const [winnersCount, setWinnersCount] = useState(3);
  const [endsAt, setEndsAt] = useState("");
  const [creating, setCreating] = useState(false);

  // entry form
  const [entryUser, setEntryUser] = useState("");
  const [entryTickets, setEntryTickets] = useState("");
  const [busyAction, setBusyAction] = useState(null);

  const load = () => {
    api.get("/raffles").then((res) => setRaffles(res.data.raffles)).catch(() => setRaffles([]));
    api.get("/admin/users").then((res) => setUsers(res.data.users)).catch(() => {});
  };

  useEffect(load, []);

  const replaceRaffle = (r) => setRaffles((prev) => prev.map((x) => (x.id === r.id ? r : x)));

  const createRaffle = async (e) => {
    e.preventDefault();
    if (!title.trim() || !prize.trim() || !endsAt) {
      toast.error("Fill in title, prize and end date");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/admin/raffles", {
        title,
        prize,
        winners_count: Number(winnersCount) || 1,
        ends_at: new Date(endsAt).toISOString(),
      });
      setRaffles((prev) => [res.data, ...(prev || [])]);
      setOpenId(res.data.id);
      setTitle("");
      setPrize("");
      setWinnersCount(3);
      setEndsAt("");
      toast.success("Raffle created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create raffle");
    } finally {
      setCreating(false);
    }
  };

  const addEntry = async (raffle) => {
    if (!entryUser || !entryTickets || Number(entryTickets) < 1) {
      toast.error("Pick a user and a ticket amount");
      return;
    }
    setBusyAction(`entry-${raffle.id}`);
    try {
      const res = await api.post(`/admin/raffles/${raffle.id}/entries`, {
        discord_id: entryUser,
        tickets: Number(entryTickets),
      });
      replaceRaffle(res.data);
      setEntryTickets("");
      toast.success("Tickets saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add tickets");
    } finally {
      setBusyAction(null);
    }
  };

  const removeEntry = async (raffle, discordId) => {
    setBusyAction(`rm-${discordId}`);
    try {
      const res = await api.delete(`/admin/raffles/${raffle.id}/entries/${discordId}`);
      replaceRaffle(res.data);
      toast.success("Entry removed");
    } catch (err) {
      toast.error("Failed to remove entry");
    } finally {
      setBusyAction(null);
    }
  };

  const drawWinners = async (raffle) => {
    setBusyAction(`draw-${raffle.id}`);
    try {
      const res = await api.post(`/admin/raffles/${raffle.id}/draw`);
      replaceRaffle(res.data);
      toast.success("Winners drawn!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Draw failed");
    } finally {
      setBusyAction(null);
    }
  };

  const deleteRaffle = async (raffle) => {
    if (!window.confirm(`Delete raffle "${raffle.title}"?`)) return;
    try {
      await api.delete(`/admin/raffles/${raffle.id}`);
      setRaffles((prev) => prev.filter((x) => x.id !== raffle.id));
      toast.success("Raffle deleted");
    } catch (err) {
      toast.error("Failed to delete raffle");
    }
  };

  return (
    <div className="space-y-6">
      {/* Create */}
      <div className="glass-card p-5 sm:p-7">
        <h3 className="font-display font-bold text-white text-[1.2rem]">Create a raffle</h3>
        <form onSubmit={createRaffle} className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div className="lg:col-span-2">
            <p className={labelCls}>Title</p>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="July golden ticket raffle" className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <p className={labelCls}>Prize</p>
            <Input value={prize} onChange={(e) => setPrize(e.target.value)} placeholder="$500" className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <p className={labelCls}>Winners</p>
            <Input type="number" min="1" max="50" value={winnersCount} onChange={(e) => setWinnersCount(e.target.value)} className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <p className={labelCls}>Ends at</p>
            <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className={`mt-2 ${inputCls} [color-scheme:dark]`} />
          </div>
          <button type="submit" disabled={creating} className="btn-pill btn-yellow !py-3 !px-6 text-[0.75rem] disabled:opacity-60 sm:col-span-2 lg:col-span-1">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create
          </button>
        </form>
      </div>

      {/* List */}
      {raffles === null ? (
        <div className="glass-card py-14 flex justify-center">
          <p className="text-[#bcd9f2]">Loading raffles...</p>
        </div>
      ) : raffles.length === 0 ? (
        <div className="glass-card py-14 flex justify-center">
          <p className="text-[#bcd9f2]">No raffles yet — create the first one above.</p>
        </div>
      ) : (
        raffles.map((raffle) => {
          const open = openId === raffle.id;
          const ended = raffle.status === "ended";
          return (
            <div key={raffle.id} className="glass-card p-5 sm:p-7">
              <button type="button" onClick={() => setOpenId(open ? null : raffle.id)} className="w-full flex items-center justify-between gap-4 text-left">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h4 className="font-display font-bold text-white text-[1.15rem]">{raffle.title}</h4>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[0.62rem] font-bold tracking-[0.16em] uppercase border ${
                        ended ? "bg-white/8 border-white/20 text-[#9fc5e8]" : "bg-emerald-400/15 border-emerald-300/30 text-emerald-200"
                      }`}
                    >
                      {ended ? "Ended" : "Live"}
                    </span>
                  </div>
                  <p className="text-[#9fc5e8] text-[0.8rem] mt-1">
                    <Gift className="w-3.5 h-3.5 inline mr-1 text-[#ffd94a]" />
                    {raffle.prize} · {raffle.winners_count} winner(s) · {raffle.entries.length} entries · ends {new Date(raffle.ends_at).toLocaleString()}
                  </p>
                </div>
                {open ? <ChevronUp className="w-5 h-5 text-white/60 shrink-0" /> : <ChevronDown className="w-5 h-5 text-white/60 shrink-0" />}
              </button>

              {open && (
                <div className="mt-6 border-t border-white/10 pt-6 space-y-6">
                  {/* Add tickets */}
                  <div className="glass-card-inner p-4 sm:p-5">
                    <p className={labelCls}>Add / update tickets</p>
                    <div className="mt-3 flex flex-col sm:flex-row gap-3">
                      <Select value={entryUser} onValueChange={setEntryUser}>
                        <SelectTrigger className={`${inputCls} sm:flex-1`}>
                          <SelectValue placeholder="Select Discord user" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a2e57] border-white/15 text-[#d7eafb]">
                          {users.length === 0 && (
                            <div className="px-3 py-2 text-[0.8rem] text-[#9fc5e8]">No signups yet</div>
                          )}
                          {users.map((u) => (
                            <SelectItem key={u.discord_id} value={u.discord_id} className="focus:bg-white/10 focus:text-white">
                              @{u.username} {u.global_name ? `(${u.global_name})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="1"
                        placeholder="Tickets"
                        value={entryTickets}
                        onChange={(e) => setEntryTickets(e.target.value)}
                        className={`${inputCls} sm:w-36`}
                      />
                      <button
                        type="button"
                        onClick={() => addEntry(raffle)}
                        disabled={busyAction === `entry-${raffle.id}`}
                        className="btn-pill btn-teal !py-3 !px-6 text-[0.72rem] disabled:opacity-60"
                      >
                        {busyAction === `entry-${raffle.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}
                        Save tickets
                      </button>
                    </div>
                  </div>

                  {/* Entries */}
                  {raffle.entries.length > 0 && (
                    <div>
                      <p className={labelCls}>Entries ({raffle.entries.length})</p>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {raffle.entries.map((en, i) => (
                          <div key={en.discord_id} className="entry-row">
                            <span className="entry-rank">#{i + 1}</span>
                            {en.avatar_url && <img src={en.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />}
                            <span className="text-white text-[0.88rem] font-bold truncate flex-1">{en.username}</span>
                            <span className="flex items-center gap-1 text-[#ffd94a] text-[0.8rem] font-bold">
                              <Ticket className="w-3.5 h-3.5" />
                              {en.tickets.toLocaleString()}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeEntry(raffle, en.discord_id)}
                              disabled={busyAction === `rm-${en.discord_id}`}
                              className="text-[#ff8ba0] hover:text-red-300 transition-colors duration-200 disabled:opacity-50"
                              title="Remove entry"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Winners */}
                  {raffle.winners.length > 0 && (
                    <div>
                      <p className={labelCls}>Winners</p>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {raffle.winners.map((w) => (
                          <div key={w.discord_id} className="entry-row !border-yellow-300/30 !bg-yellow-300/5">
                            <Trophy className="w-4 h-4 text-[#ffd94a]" />
                            <span className="entry-rank">#{w.place}</span>
                            <span className="text-white text-[0.88rem] font-bold truncate flex-1">{w.username}</span>
                            <span className="text-[#ffd94a] text-[0.8rem] font-bold">{w.tickets.toLocaleString()} tickets</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => drawWinners(raffle)}
                      disabled={busyAction === `draw-${raffle.id}` || raffle.entries.length === 0}
                      className="btn-pill btn-yellow !py-3 !px-6 text-[0.72rem] disabled:opacity-50"
                    >
                      {busyAction === `draw-${raffle.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                      {ended ? "Redraw winners" : "Draw winners now"}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteRaffle(raffle)}
                      className="btn-pill btn-ghost !py-3 !px-6 text-[0.72rem] !text-[#ff8ba0] hover:!border-red-300/40"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default AdminRaffles;

